import React, { useState } from 'react';
import {
  Box, CircularProgress, Typography, Alert, Stack, Button,
  Checkbox, FormControlLabel, Pagination, FormControl, Select, MenuItem,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import FilterBar from '../components/FilterBar';
import JobCard from '../components/JobCard';
import SourceChip from '../components/SourceChip';
import { searchJobs, loadMoreJobs, saveJobs } from '../api/jobsApi';
import { clearAuth, getUser } from '../utils/auth';
import { ACCENT } from '../components/DashboardLayout';

const ROLE_LABELS = {
  ADMIN: 'Admin',
  TEAM_LEAD: 'Team Lead',
  RECRUITER: 'Recruiter',
};

const ROLE_HOME_ROUTES = {
  ADMIN: '/admin',
  TEAM_LEAD: '/team-lead/home',
  RECRUITER: '/recruiter/home',
};

const PAGE_SIZE_OPTIONS = [5, 10, 20, 30];
const DEFAULT_PAGE_SIZE = 10;

// The fields that actually change what gets searched. page/pageSize aren't part of
// this — changing just the page size shouldn't count as "a new search".
const FILTER_KEYS = ['jobTitle', 'location', 'industry', 'experience', 'postedWithinDays'];
function getJobKey(job, index) {
  return job.id || job.url || `job-${index}`;
}

function sameSearch(a, b) {
  if (!a || !b) return false;
  return FILTER_KEYS.every((key) => (a[key] || '') === (b[key] || ''));
}

export default function JobSearchPage() {
  const navigate = useNavigate();
  const user = getUser();
  const canSave = user?.role === 'TEAM_LEAD' || user?.role === 'ADMIN';
  const roleLabel = ROLE_LABELS[user?.role] || 'User';

  const [jobs, setJobs] = useState([]);
  const [lastFilters, setLastFilters] = useState(null);
  const [sourceBreakdown, setSourceBreakdown] = useState({});
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  const handleLogout = () => {
    clearAuth();
    navigate('/');
  };

  const mergeSourceBreakdown = (prev, incoming) => {
    const merged = { ...prev };
    Object.entries(incoming).forEach(([source, count]) => {
      merged[source] = (merged[source] || 0) + count;
    });
    return merged;
  };

  // Fetches exactly ONE more batch and appends it — no looping, no "keep fetching until
  // the page is full." A batch that only yields 1 matching job after local filtering
  // (e.g. a narrow Min/Max applicants range) shows that 1 job; the user clicks Apply/Load
  // more again to spend on fetching the next batch, on their own choice, rather than the
  // app silently spending on their behalf trying to fill a page that a narrow filter might
  // never fill.
  const fetchNextBatch = async (currentCursor) => {
    const data = await loadMoreJobs(currentCursor);
    setJobs((prev) => [...prev, ...data.jobs]);
    setSourceBreakdown((prev) => mergeSourceBreakdown(prev, data.sourceBreakdown || {}));
    setCursor(data.nextCursor);
    setHasMore(data.hasMore);
    return data;
  };

  const handleApply = async (appliedFilters) => {
    setError('');
    setSaveMessage('');
    setSelectedKeys(new Set());

    // Re-applying the exact same filters is treated as "show me more", not "start over" —
    // otherwise you'd just see the same first batch of jobs again. This still fetches only
    // ONE batch, same as a fresh search does.
    if (sameSearch(lastFilters, appliedFilters)) {
      if (!cursor) {
        setError('No more jobs match these filters.');
        return;
      }
      setLoading(true);
      try {
        const jobsLoadedBefore = jobs.length;
        await fetchNextBatch(cursor);
        setPage(Math.floor(jobsLoadedBefore / pageSize) + 1);
      } catch (err) {
        setError('Could not fetch more jobs. Please try again.');
      } finally {
        setLoading(false);
        setSearched(true);
      }
      return;
    }

    setLoading(true);
    setPage(1);
    try {
      const data = await searchJobs(appliedFilters);
      setJobs(data.jobs);
      setLastFilters(appliedFilters);
      setSourceBreakdown(data.sourceBreakdown || {});
      setCursor(data.nextCursor);
      setHasMore(data.hasMore);
      // No top-up loop here anymore — page 1 shows exactly what this one batch produced,
      // even if that's fewer jobs than the chosen page size.
    } catch (err) {
      setError('Could not fetch jobs. Please try again.');
    } finally {
      setLoading(false);
      setSearched(true);
    }
  };

  const toggleJob = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const allSelected = jobs.length > 0 && selectedKeys.size === jobs.length;
  const someSelected = selectedKeys.size > 0 && !allSelected;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedKeys(new Set());
    } else {
      setSelectedKeys(new Set(jobs.map((job, index) => getJobKey(job, index))));
    }
  };

  const handleSaveSelected = async () => {
    const selectedJobs = jobs.filter((job, index) => selectedKeys.has(getJobKey(job, index)));
    if (selectedJobs.length === 0) return;

    setSaving(true);
    setSaveMessage('');
    setError('');
    try {
      const data = await saveJobs(selectedJobs, lastFilters?.location, lastFilters?.industry);
      setSaveMessage(data.message);
      setSelectedKeys(new Set());
    } catch (err) {
      setError('Could not save the selected jobs. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Pagination is driven off whatever's been loaded so far, plus one extra page whenever
  // there's more upstream — clicking into that extra page transparently fetches ONE more
  // batch (not a top-up loop), same credit-conscious rule as everywhere else on this page.
  const loadedPages = Math.max(1, Math.ceil(jobs.length / pageSize));
  const totalPages = loadedPages + (hasMore ? 1 : 0);
  const pagedJobs = jobs.slice((page - 1) * pageSize, page * pageSize);

  const handlePageChange = async (event, value) => {
    const neededCount = value * pageSize;

    if (neededCount <= jobs.length) {
      setPage(value);
      return;
    }

    setLoadingMore(true);
    setError('');
    try {
      await fetchNextBatch(cursor);
      setPage(value);
    } catch (err) {
      setError('Could not fetch more jobs. Please try again.');
    } finally {
      setLoadingMore(false);
    }
  };

  const handlePageSizeChange = (event) => {
    const newPageSize = event.target.value;
    setPageSize(newPageSize);
    setPage(1);
    // No top-up fetch on page size change anymore — if the bigger page size isn't fully
    // populated by what's already loaded, the page just shows fewer jobs than the chosen
    // size, rather than automatically spending credits to fill it.
  };

  return (
    <Box sx={{ maxWidth: '100%', mx: 'auto', p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 2,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '1.2px',
              color: ACCENT,
              mb: 0.5,
              textTransform: 'uppercase',
            }}
          >
            {roleLabel}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Lora', serif",
              fontWeight: 600,
              fontSize: 26,
              color: '#1B2430',
              lineHeight: 1.2,
            }}
          >
            Search Jobs
          </Typography>
          <Typography variant="body2" sx={{ color: '#5B6472', mt: 0.5 }}>
            {roleLabel} — {user?.email}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(ROLE_HOME_ROUTES[user?.role] || '/')}
            sx={{
              color: '#16324F',
              borderColor: '#C9CDD4',
              '&:hover': { borderColor: '#16324F', background: '#F2F4F6' },
            }}
          >
            ← Back to Home
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleLogout}
            sx={{
              color: '#16324F',
              borderColor: '#C9CDD4',
              '&:hover': { borderColor: '#16324F', background: '#F2F4F6' },
            }}
          >
            Log Out
          </Button>
        </Stack>
      </Box>

      <FilterBar onApply={handleApply} />
      {(loading || loadingMore) && <CircularProgress size={28} />}
      {error && <Alert severity="error">{error}</Alert>}
      {saveMessage && <Alert severity="success" sx={{ mt: 2 }}>{saveMessage}</Alert>}

      {!loading && !error && searched && (
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            rowGap: 1.5,
            mt: 2,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ rowGap: 1 }}>
            <Typography variant="body2" fontWeight={600} color="text.primary">
              {jobs.length} job{jobs.length === 1 ? '' : 's'} loaded so far
            </Typography>
            {Object.entries(sourceBreakdown).map(([source, count]) => (
              <SourceChip key={source} source={source} count={count} />
            ))}
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select value={pageSize} onChange={handlePageSizeChange} sx={{ fontSize: 13, height: 30 }}>
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <MenuItem key={size} value={size}>{size} per page</MenuItem>
                ))}
              </Select>
            </FormControl>

            {totalPages > 1 && (
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="small"
                disabled={loadingMore}
              />
            )}
          </Stack>
        </Box>
      )}

      {!loading && !error && searched && jobs.length > 0 && canSave && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            my: 1.5,
            flexWrap: 'wrap',
            rowGap: 1,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={toggleSelectAll}
              />
            }
            label={`Select all loaded (${jobs.length})`}
          />
          <Button
            variant="contained"
            size="small"
            disabled={selectedKeys.size === 0 || saving}
            onClick={handleSaveSelected}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : `Save selected (${selectedKeys.size})`}
          </Button>
        </Box>
      )}

      {!loading && !error && searched && jobs.length === 0 && (
        <Typography color="text.secondary">No jobs matched these filters.</Typography>
      )}
      {!loading && !error && !searched && (
        <Typography color="text.secondary">No jobs yet — apply a filter to search.</Typography>
      )}

      {pagedJobs.map((job) => {
        const index = jobs.indexOf(job);
        const key = getJobKey(job, index);
        return (
          <Box key={key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            {canSave && (
              <Checkbox
                checked={selectedKeys.has(key)}
                onChange={() => toggleJob(key)}
                sx={{ mt: 1.5 }}
              />
            )}
            <Box sx={{ flexGrow: 1 }}>
              <JobCard job={job} />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}